/**
 * Mock API Data for Development
 * 
 * Provides realistic mock data for frontend development without a backend.
 * Used when VITE_USE_MOCK_API=true in .env.development
 */

import type { DashboardOverview, UsageDetails, ActivityFeed, AnalyticsResponse } from '../dashboard/useDashboard';
import { handleMockRequest, isMandateMockEndpoint } from '../mandate/mockApi';

// ============================================================================
// Dashboard Mock Data
// ============================================================================

export const mockDashboardOverview: DashboardOverview = {
  subscription: {
    tier: 'professional',
    status: 'active',
    current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    limits: {
      max_concurrent_backtests: 5,
      max_strategies: 20,
      historical_data_months: 24,
      api_rate_limit: 1000,
    },
  },
  usage: {
    backtests_this_month: 47,
    active_backtests: 2,
    strategies_count: 8,
    api_calls_this_hour: 156,
    storage_used_mb: 1240,
  },
  stats: {
    period: 'month',
    total_backtests: 47,
    successful_backtests: 42,
    failed_backtests: 5,
    avg_backtest_duration_secs: 245,
    most_tested_symbol: 'BTC/USDT',
    best_sharpe_ratio: 2.34,
  },
  recent_backtests: [
    {
      id: 'bt-001',
      symbol: 'BTC/USDT',
      strategy: 'Momentum Breakout',
      status: 'completed',
      sharpe_ratio: 1.85,
      total_return_pct: 24.5,
      oos_sharpe_ratio: 1.42,
      walk_forward_enabled: true,
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'bt-002',
      symbol: 'ETH/USDT',
      strategy: 'Mean Reversion',
      status: 'processing',
      walk_forward_enabled: false,
      created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    },
    {
      id: 'bt-003',
      symbol: 'SOL/USDT',
      strategy: 'Grid Trading',
      status: 'completed',
      sharpe_ratio: 2.12,
      total_return_pct: 18.3,
      oos_sharpe_ratio: 1.97,
      walk_forward_enabled: true,
      created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      completed_at: new Date(Date.now() - 23 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'bt-004',
      symbol: 'AVAX/USDT',
      strategy: 'Trend Following',
      status: 'failed',
      walk_forward_enabled: false,
      created_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'bt-005',
      symbol: 'BTC/USDT',
      strategy: 'Scalping',
      status: 'queued',
      walk_forward_enabled: false,
      created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    },
  ],
};

export const mockUsageDetails: UsageDetails = {
  current_period: {
    period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    period_end: new Date().toISOString(),
    total_backtests: 47,
    compute_minutes: 1250,
    api_calls: 15420,
    data_processed_gb: 8.5,
  },
  previous_period: {
    period_start: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    period_end: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    total_backtests: 38,
    compute_minutes: 980,
    api_calls: 12100,
    data_processed_gb: 6.2,
  },
  daily_breakdown: Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] || '',
    backtests: Math.floor(Math.random() * 5) + 1,
    api_calls: Math.floor(Math.random() * 800) + 200,
  })),
  by_symbol: [
    { symbol: 'BTC/USDT', backtest_count: 18, avg_sharpe: 1.65 },
    { symbol: 'ETH/USDT', backtest_count: 12, avg_sharpe: 1.42 },
    { symbol: 'SOL/USDT', backtest_count: 8, avg_sharpe: 1.88 },
    { symbol: 'AVAX/USDT', backtest_count: 5, avg_sharpe: 1.21 },
    { symbol: 'MATIC/USDT', backtest_count: 4, avg_sharpe: 1.55 },
  ],
  by_strategy: [
    { strategy: 'Momentum Breakout', backtest_count: 15, avg_profit_factor: 1.82 },
    { strategy: 'Mean Reversion', backtest_count: 12, avg_profit_factor: 1.65 },
    { strategy: 'Grid Trading', backtest_count: 10, avg_profit_factor: 1.45 },
    { strategy: 'Trend Following', backtest_count: 6, avg_profit_factor: 1.92 },
    { strategy: 'Scalping', backtest_count: 4, avg_profit_factor: 1.28 },
  ],
};

export const mockActivityFeed: ActivityFeed = {
  activities: [
    {
      id: 'act-001',
      activity_type: 'backtest_completed',
      title: 'Backtest Completed',
      description: 'BTC/USDT Momentum Breakout completed with Sharpe 1.85',
      metadata: { symbol: 'BTC/USDT', sharpe: 1.85 },
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'act-002',
      activity_type: 'backtest_started',
      title: 'Backtest Started',
      description: 'ETH/USDT Mean Reversion backtest initiated',
      metadata: { symbol: 'ETH/USDT' },
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    },
    {
      id: 'act-003',
      activity_type: 'strategy_created',
      title: 'Strategy Created',
      description: 'New strategy "Adaptive RSI" created',
      metadata: { strategy_name: 'Adaptive RSI' },
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'act-004',
      activity_type: 'api_key_created',
      title: 'API Key Created',
      description: 'New API key "Production Bot" generated',
      metadata: { key_name: 'Production Bot' },
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'act-005',
      activity_type: 'backtest_failed',
      title: 'Backtest Failed',
      description: 'AVAX/USDT Trend Following failed: insufficient data',
      metadata: { symbol: 'AVAX/USDT', error: 'insufficient data' },
      timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    },
  ],
  has_more: true,
  next_cursor: 'cursor-page-2',
};

export const mockAnalytics: AnalyticsResponse = {
  performance: {
    avg_sharpe_ratio: 1.58,
    avg_sortino_ratio: 2.12,
    avg_max_drawdown: 0.12,
    avg_win_rate: 0.58,
    best_performing_strategy: 'Trend Following',
    best_performing_symbol: 'SOL/USDT',
    total_simulated_pnl: 125840,
  },
  optimization: {
    total_generations_run: 2450,
    avg_generations_per_backtest: 52,
    convergence_rate: 0.85,
    most_optimized_params: ['risk_aversion', 'lookback_period', 'threshold'],
  },
  trends: {
    backtest_volume_trend: 'up',
    performance_trend: 'stable',
    weekly_backtest_counts: [8, 12, 10, 15, 11, 14, 12],
  },
};

// ============================================================================
// Strategies Mock Data
// ============================================================================

export interface MockStrategy {
  id: string;
  strategy_name: string;
  strategy_type: string;
  version: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  instance_count: number;
  performance_summary: {
    total_pnl?: number;
    sharpe_ratio?: number;
    sortino_ratio?: number;
    max_drawdown?: number;
    win_rate?: number;
    profit_factor?: number;
    total_trades?: number;
  } | null;
}

export const mockStrategies: MockStrategy[] = [
  {
    id: 'strat-001',
    strategy_name: 'Momentum Breakout',
    strategy_type: 'custom',
    version: '2.1.0',
    description: 'Captures breakouts from consolidation patterns using volume confirmation',
    is_active: true,
    created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    instance_count: 3,
    performance_summary: {
      total_pnl: 45200,
      sharpe_ratio: 1.85,
      sortino_ratio: 2.45,
      max_drawdown: 0.08,
      win_rate: 0.62,
      profit_factor: 1.92,
      total_trades: 156,
    },
  },
  {
    id: 'strat-002',
    strategy_name: 'Mean Reversion RSI',
    strategy_type: 'custom',
    version: '1.5.2',
    description: 'RSI-based mean reversion with dynamic bands',
    is_active: true,
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    instance_count: 2,
    performance_summary: {
      total_pnl: 28500,
      sharpe_ratio: 1.42,
      sortino_ratio: 1.88,
      max_drawdown: 0.12,
      win_rate: 0.58,
      profit_factor: 1.65,
      total_trades: 234,
    },
  },
  {
    id: 'strat-003',
    strategy_name: 'Grid Trading Bot',
    strategy_type: 'custom',
    version: '3.0.0',
    description: 'Automated grid trading with adaptive spacing',
    is_active: true,
    created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    instance_count: 5,
    performance_summary: {
      total_pnl: 18900,
      sharpe_ratio: 2.12,
      sortino_ratio: 2.85,
      max_drawdown: 0.05,
      win_rate: 0.72,
      profit_factor: 1.45,
      total_trades: 892,
    },
  },
  {
    id: 'strat-004',
    strategy_name: 'Trend Following MACD',
    strategy_type: 'custom',
    version: '1.2.0',
    description: 'MACD crossover with trend filters',
    is_active: false,
    created_at: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    instance_count: 1,
    performance_summary: {
      total_pnl: -2400,
      sharpe_ratio: 0.45,
      sortino_ratio: 0.52,
      max_drawdown: 0.22,
      win_rate: 0.38,
      profit_factor: 0.85,
      total_trades: 67,
    },
  },
  {
    id: 'strat-005',
    strategy_name: 'Scalping Alpha',
    strategy_type: 'custom',
    version: '1.0.0',
    description: 'High-frequency scalping for liquid pairs',
    is_active: true,
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    instance_count: 1,
    performance_summary: null,
  },
];

// ============================================================================
// Backtests Mock Data
// ============================================================================

export interface MockBacktestJob {
  id: string;
  job_id: string;
  symbol: string;
  exchange: string;
  strategy_type: string;
  status: 'pending' | 'queued' | 'running' | 'processing' | 'completed' | 'failed';
  progress: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  current_generation?: number;
  total_generations?: number;
  current_phase?: string;
  sharpe_ratio?: number;
  max_drawdown?: number;
  total_pnl?: number;
  win_rate?: number;
}

export const mockBacktestJobs: MockBacktestJob[] = [
  {
    id: 'job-001',
    job_id: 'job-1',
    symbol: 'BTC-USD',
    exchange: 'massive',
    strategy_type: 'custom',
    status: 'completed',
    progress: 100,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    current_generation: 50,
    total_generations: 50,
    current_phase: 'completed',
    sharpe_ratio: 1.5,
    max_drawdown: -0.15,
    total_pnl: 5000,
    win_rate: 0.65,
  },
  {
    id: 'job-002',
    job_id: 'job-2',
    symbol: 'ETH-USD',
    exchange: 'massive',
    strategy_type: 'custom',
    status: 'queued',
    progress: 0,
    created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    current_phase: 'queued',
  },
  {
    id: 'job-003',
    job_id: 'job-3',
    symbol: 'BTC-USD',
    exchange: 'massive',
    strategy_type: 'custom',
    status: 'running',
    progress: 50,
    created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    current_phase: 'Genetic Optimization',
    current_generation: 10,
    total_generations: 20,
  },
  {
    id: 'job-004',
    job_id: 'job-4',
    symbol: 'AVAX-USD',
    exchange: 'massive',
    strategy_type: 'custom',
    status: 'failed',
    progress: 15,
    created_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    error_message: 'Insufficient historical data for requested date range',
    current_phase: 'failed',
  },
];

export interface MockBacktestResult {
  id: string;
  job_id: string;
  strategy_name: string;
  symbol: string;
  exchange: string;
  total_return: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  max_drawdown: number;
  total_trades: number;
  win_rate: number;
  profit_factor: number;
  avg_trade_duration_mins: number;
  best_trade_pct: number;
  worst_trade_pct: number;
  monthly_returns: number[];
  equity_curve: { timestamp: string; equity: number }[];
  total_pnl?: number;
  calmar_ratio?: number;
  initial_capital?: number;
  result_quality?: string;
  monte_carlo?: {
    num_runs: number;
    mean_sharpe: number;
    stddev_sharpe: number;
    var_95: number;
    cvar_95: number;
    confidence_lower: number;
    confidence_upper: number;
    p5_total_pnl?: number;
    p50_total_pnl?: number;
    p95_total_pnl?: number;
    p5_max_drawdown?: number;
    p50_max_drawdown?: number;
    p95_max_drawdown?: number;
    p50_sharpe?: number;
    fan_chart?: number[][];
  };
  walk_forward?: {
    periods_tested: number;
    average_performance: number;
    consistency_score: number;
    out_of_sample_degradation: number;
    oos_total_pnl?: number;
    oos_sharpe_ratio?: number;
    oos_max_drawdown?: number;
    oos_win_rate?: number;
    oos_profit_factor?: number;
    oos_num_trades?: number;
    oos_equity_curve?: number[];
  };
  statistical_significance?: {
    p_value: number;
    t_statistic: number;
    is_significant: boolean;
    confidence_level: number;
  };
  ga_report?: {
    best_fitness: number;
    best_params: Record<string, unknown>;
    top_n_params: Array<{ params: Record<string, unknown>; fitness: number; distance_from_best: number }>;
    sensitivity: Array<{
      param_name: string;
      fitness_minus_25: number;
      fitness_minus_10: number;
      fitness_at_optimal: number;
      fitness_plus_10: number;
      fitness_plus_25: number;
      sensitivity_score: number;
    }>;
    convergence_curve: Array<{ generation: number; best_fitness: number; mean_fitness: number }>;
    landscapes?: Array<{
      param_a_name: string;
      param_b_name: string;
      grid_size: number;
      grid: Array<{ param_a_val: number; param_b_val: number; fitness: number }>;
      flatness_score: number;
    }>;
    overfitting?: {
      train_sharpe: number;
      test_sharpe: number;
      degradation_pct: number;
      is_overfit: boolean;
    };
    // Legacy fields kept for potential old code paths
    generations_run?: number;
    population_size?: number;
    convergence_history?: number[];
    best_parameters?: Record<string, number>;
  };
  trade_log?: Array<{
    entry_time: string;
    exit_time: string;
    side: string;
    entry_price: number;
    exit_price: number;
    size: number;
    pnl: number;
    reason: string;
  }>;
}

// Helper: generate realistic trade log entries
function generateMockTrades(count: number, baseTime: number): MockBacktestResult['trade_log'] {
  const trades: NonNullable<MockBacktestResult['trade_log']> = [];
  let t = baseTime;
  for (let i = 0; i < count; i++) {
    const side = Math.random() > 0.5 ? 'long' : 'short';
    const entryPrice = 40000 + Math.random() * 10000;
    const pnlPct = (Math.random() - 0.38) * 0.04;
    const exitPrice = entryPrice * (1 + (side === 'long' ? pnlPct : -pnlPct));
    const size = 0.01 + Math.random() * 0.05;
    const reasons = ['bollinger_rsi_entry', 'trailing_stop_exit', 'mean_reversion_exit', 'max_hold_exit', 'rsi_oversold_entry'];
    trades.push({
      entry_time: new Date(t).toISOString(),
      exit_time: new Date(t + (15 + Math.random() * 480) * 60_000).toISOString(),
      side,
      entry_price: +entryPrice.toFixed(2),
      exit_price: +exitPrice.toFixed(2),
      size: +size.toFixed(4),
      pnl: +(size * (exitPrice - entryPrice) * (side === 'long' ? 1 : -1)).toFixed(2),
      reason: reasons[i % reasons.length]!,
    });
    t += (30 + Math.random() * 120) * 60_000;
  }
  return trades;
}

// BTC price keypoints (day offset from 2023-01-01, USD price)
// Approximates real BTC price action through the 2023-2024 cycle.
const BTC_KP: [number, number][] = [
  [0, 16530], [30, 23000], [60, 23500], [90, 29000], [120, 27500],
  [150, 30500], [180, 29800], [210, 26100], [240, 25900], [270, 27000],
  [300, 35000], [330, 42000], [365, 44200], [395, 52000], [425, 62000],
  [455, 72000], [485, 63000], [515, 68000], [548, 62000], [570, 56000],
  [600, 57000], [630, 63000], [660, 68000], [700, 96000], [729, 97000],
];
const BTC_UNITS = 10000 / 16530;

function btcEquityAt(day: number): number {
  let k0 = BTC_KP[0]![0], p0 = BTC_KP[0]![1];
  let k1 = BTC_KP[1]![0], p1 = BTC_KP[1]![1];
  for (let k = 0; k < BTC_KP.length - 1; k++) {
    if (day >= BTC_KP[k]![0] && day <= BTC_KP[k + 1]![0]) {
      k0 = BTC_KP[k]![0]; p0 = BTC_KP[k]![1];
      k1 = BTC_KP[k + 1]![0]; p1 = BTC_KP[k + 1]![1];
      break;
    }
  }
  const t = k1 === k0 ? 0 : (day - k0) / (k1 - k0);
  const price = p0 + (p1 - p0) * t;
  const noise = 1 + Math.sin(day * 0.31 + 1.4) * 0.009 + Math.cos(day * 0.19 + 0.7) * 0.006;
  return Math.round(BTC_UNITS * price * noise * 100) / 100;
}

export const mockBacktestResults: Record<string, MockBacktestResult> = {
  'job-001': {
    id: 'result-001',
    job_id: 'job-001',
    strategy_name: 'BTC Buy & Hold',
    symbol: 'BTC/USDT',
    exchange: 'Kraken',
    total_return: 4.868,
    total_pnl: 48680,
    initial_capital: 10000,
    sharpe_ratio: 1.82,
    sortino_ratio: 2.86,
    calmar_ratio: 4.87,
    max_drawdown: 0.30,
    total_trades: 1,
    win_rate: 1.0,
    profit_factor: 99,
    avg_trade_duration_mins: 730 * 24 * 60,
    best_trade_pct: 4.868,
    worst_trade_pct: 4.868,
    result_quality: 'sufficient',
    monthly_returns: [
      0.396, 0.000, 0.232, 0.026, -0.076, 0.118,
      -0.052, -0.113, 0.036, 0.280, 0.092, -0.031,
      -0.032, 0.458, 0.174, 0.071, -0.054, 0.100,
      -0.028, -0.081, 0.100, 0.131, 0.373, 0.086,
    ],
    equity_curve: Array.from({ length: 730 }, (_, i) => ({
      timestamp: new Date(new Date('2023-01-01').getTime() + i * 86400000).toISOString(),
      equity: btcEquityAt(i),
    })),
    monte_carlo: {
      num_runs: 1000,
      mean_sharpe: 1.61,
      stddev_sharpe: 0.58,
      var_95: -1200,
      cvar_95: -2100,
      confidence_lower: 0.82,
      confidence_upper: 2.88,
      p5_total_pnl: 3500,
      p50_total_pnl: 43000,
      p95_total_pnl: 154000,
      p5_max_drawdown: 0.08,
      p50_max_drawdown: 0.28,
      p95_max_drawdown: 0.62,
      p50_sharpe: 1.55,
      fan_chart: Array.from({ length: 100 }, (_, i) => {
        const t = Math.pow(i / 99, 0.7);
        return [
          Math.round(10000 + 3500 * t),
          Math.round(10000 + 18000 * t),
          Math.round(10000 + 43000 * t),
          Math.round(10000 + 84000 * t),
          Math.round(10000 + 154000 * t),
        ];
      }),
    },
    walk_forward: {
      periods_tested: 1,
      average_performance: 1.43,
      consistency_score: 1.0,
      out_of_sample_degradation: 0.21,
      oos_total_pnl: 21173,
      oos_sharpe_ratio: 1.43,
      oos_max_drawdown: 0.10,
      oos_win_rate: 1.0,
      oos_profit_factor: 99,
      oos_num_trades: 1,
      oos_equity_curve: Array.from({ length: 182 }, (_, i) => btcEquityAt(548 + i)),
    },
    trade_log: generateMockTrades(1, new Date('2023-01-01').getTime()),
  },
};

// ============================================================================
// Mock API Response Handler
// ============================================================================

/**
 * Get mock data for an API endpoint
 */
export function getMockResponse(endpoint: string, method: string = 'GET', body?: unknown): unknown {
  // Mandates, documented-strategy library and proposals: a small stateful mock
  // that needs the request body (see src/mandate/mockApi.ts).
  if (isMandateMockEndpoint(endpoint)) {
    return handleMockRequest(endpoint, method, body);
  }

  // Symbol search endpoint
  if (endpoint.startsWith('/api/data/symbols/search')) {
    const urlParams = new URLSearchParams(endpoint.split('?')[1] ?? '');
    const q = urlParams.get('q')?.toLowerCase() ?? '';
    const limit = parseInt(urlParams.get('limit') ?? '20', 10);
    const allSymbols = [
      'BTC-USD', 'ETH-USD', 'SOL-USD', 'XRP-USD', 'BNB-USD', 'ADA-USD',
      'AVAX-USD', 'DOGE-USD', 'LINK-USD', 'DOT-USD', 'MATIC-USD', 'LTC-USD',
      'BCH-USD', 'ATOM-USD', 'UNI-USD', 'FIL-USD', 'APT-USD', 'ARB-USD',
      'OP-USD', 'INJ-USD', 'SUI-USD', 'NEAR-USD', 'TIA-USD', 'SEI-USD',
    ];
    const filtered = q ? allSymbols.filter((s) => s.toLowerCase().includes(q)) : allSymbols;
    return { data: filtered.slice(0, limit).map((symbol) => ({ symbol })) };
  }

  // Top symbols endpoint (pre-populated popular symbols per exchange)
  if (endpoint.startsWith('/api/data/symbols/top')) {
    const urlParams = new URLSearchParams(endpoint.split('?')[1] ?? '');
    const limit = parseInt(urlParams.get('limit') ?? '20', 10);
    const allSymbols = [
      'BTC-USD', 'ETH-USD', 'SOL-USD', 'XRP-USD', 'BNB-USD', 'ADA-USD',
      'AVAX-USD', 'DOGE-USD', 'LINK-USD', 'DOT-USD', 'MATIC-USD', 'LTC-USD',
      'BCH-USD', 'ATOM-USD', 'UNI-USD', 'FIL-USD', 'APT-USD', 'ARB-USD',
      'OP-USD', 'INJ-USD', 'SUI-USD', 'NEAR-USD', 'TIA-USD', 'SEI-USD',
    ];
    return { data: allSymbols.slice(0, limit).map((symbol) => ({ symbol })) };
  }

  // Exchange metadata endpoints (used by BacktestForm and JobList)
  if (endpoint.startsWith('/api/data/exchanges') || endpoint.startsWith('/api/exchanges')) {
    return {
      data: [
        { id: 'xcbt', name: 'Coinbase', symbols: [] },
        { id: 'xkrk', name: 'Kraken', symbols: [] },
        { id: 'xbns', name: 'Binance US', symbols: [] },
        { id: 'xgem', name: 'Gemini', symbols: [] },
      ]
    };
  }

  // Dashboard endpoints
  if (endpoint.startsWith('/api/dashboard/overview')) {
    return mockDashboardOverview;
  }
  if (endpoint.startsWith('/api/dashboard/usage')) {
    return mockUsageDetails;
  }
  if (endpoint.startsWith('/api/dashboard/activity')) {
    return mockActivityFeed;
  }
  if (endpoint.startsWith('/api/dashboard/analytics')) {
    return mockAnalytics;
  }

  // Strategies endpoints
  if (endpoint.startsWith('/api/strategies') && method === 'GET') {
    const includeInactive = endpoint.includes('include_inactive=true');
    const filtered = includeInactive 
      ? mockStrategies 
      : mockStrategies.filter(s => s.is_active);
    return { strategies: filtered, total: filtered.length };
  }

  // Backtests endpoints
  if (endpoint.startsWith('/api/backtests') && method === 'GET') {
    if (endpoint.includes('/results')) {
      const jobId = endpoint.match(/\/api\/backtests\/([^/]+)\/results/)?.[1];
      return jobId && mockBacktestResults[jobId] ? mockBacktestResults[jobId] : null;
    }
    return { backtests: mockBacktestJobs, total: mockBacktestJobs.length };
  }

  // Default: return empty object
  console.warn(`[MockAPI] No mock data for ${method} ${endpoint}`);
  return {};
}

/**
 * Check if an endpoint has mock data available
 */
export function hasMockData(endpoint: string): boolean {
  const mockEndpoints = [
    '/api/dashboard/',
    '/api/strategies',
    '/api/backtests',
    '/api/data/',
    '/api/exchanges/',
  ];
  return isMandateMockEndpoint(endpoint) || mockEndpoints.some(prefix => endpoint.startsWith(prefix));
}
